import { useState } from 'react';
import { Filter, X, ChevronDown } from 'lucide-react';
import { Button, Input, Card } from '../common';
import { spreads } from '../../data/spreads';

export interface SearchFilters {
  query: string;
  dateRange: {
    start: string;
    end: string;
  };
  spreadTypes: string[];
  favoriteOnly: boolean;
  privateOnly: boolean;
  tags: string[];
  sortBy: 'newest' | 'oldest' | 'favorites' | 'spread' | 'question';
  sortOrder: 'asc' | 'desc';
}

interface AdvancedSearchFiltersProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  availableTags: string[];
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

export default function AdvancedSearchFilters({
  filters,
  onFiltersChange,
  availableTags,
  isExpanded,
  onToggleExpanded
}: AdvancedSearchFiltersProps) {
  const [selectedTag, setSelectedTag] = useState('');

  const updateFilters = (updates: Partial<SearchFilters>) => {
    onFiltersChange({ ...filters, ...updates });
  };

  const addTag = (tag: string) => {
    if (tag && !filters.tags.includes(tag)) {
      updateFilters({ tags: [...filters.tags, tag] });
      setSelectedTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    updateFilters({ tags: filters.tags.filter(tag => tag !== tagToRemove) });
  };

  const toggleSpreadType = (spreadId: string) => {
    const newSpreadTypes = filters.spreadTypes.includes(spreadId)
      ? filters.spreadTypes.filter(id => id !== spreadId)
      : [...filters.spreadTypes, spreadId];
    updateFilters({ spreadTypes: newSpreadTypes });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      query: '',
      dateRange: { start: '', end: '' },
      spreadTypes: [],
      favoriteOnly: false,
      privateOnly: false,
      tags: [],
      sortBy: 'newest',
      sortOrder: 'desc'
    });
  };

  const hasActiveFilters = 
    filters.query ||
    filters.dateRange.start ||
    filters.dateRange.end ||
    filters.spreadTypes.length > 0 ||
    filters.favoriteOnly ||
    filters.privateOnly ||
    filters.tags.length > 0 ||
    filters.sortBy !== 'newest';

  return (
    <Card className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-primary-500" />
          <h3 className="font-semibold text-white">Advanced Search & Filters</h3>
          {hasActiveFilters && (
            <span className="px-2 py-1 bg-primary-500/20 text-primary-400 text-xs rounded-full">
              Active
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-gray-400 hover:text-white"
            >
              Clear All
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleExpanded}
            className="text-gray-400 hover:text-white"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Basic Search */}
      <div className="mb-4">
        <Input
          placeholder="Search questions, interpretations, or content..."
          value={filters.query}
          onChange={(e) => updateFilters({ query: e.target.value })}
          fullWidth
        />
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="space-y-6">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Date Range
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">From</label>
                <Input
                  type="date"
                  value={filters.dateRange.start}
                  onChange={(e) => updateFilters({ 
                    dateRange: { ...filters.dateRange, start: e.target.value }
                  })}
                  fullWidth
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">To</label>
                <Input
                  type="date"
                  value={filters.dateRange.end}
                  onChange={(e) => updateFilters({ 
                    dateRange: { ...filters.dateRange, end: e.target.value }
                  })}
                  fullWidth
                />
              </div>
            </div>
          </div>

          {/* Spread Types */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Spread Types
            </label>
            <div className="flex flex-wrap gap-2">
              {spreads.map((spread) => (
                <button
                  key={spread.id}
                  onClick={() => toggleSpreadType(spread.id)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filters.spreadTypes.includes(spread.id)
                      ? 'bg-primary-500 text-white'
                      : 'bg-dark-600 text-gray-300 hover:bg-dark-500'
                  }`}
                >
                  {spread.name}
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Tags
            </label>
            
            {/* Add Tag */}
            <div className="flex gap-2 mb-3">
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="flex-1 px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a tag...</option>
                {availableTags
                  .filter(tag => !filters.tags.includes(tag))
                  .map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))
                }
              </select>
              <Button
                size="sm"
                onClick={() => addTag(selectedTag)}
                disabled={!selectedTag}
              >
                Add
              </Button>
            </div>

            {/* Selected Tags */}
            {filters.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {filters.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-primary-500/20 text-primary-400 text-sm rounded-full"
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      className="hover:text-primary-300"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Quick Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Quick Filters
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => updateFilters({ favoriteOnly: !filters.favoriteOnly })}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  filters.favoriteOnly
                    ? 'bg-red-500 text-white'
                    : 'bg-dark-600 text-gray-300 hover:bg-dark-500'
                }`}
              >
                ❤️ Favorites Only
              </button>
              
              <button
                onClick={() => updateFilters({ privateOnly: !filters.privateOnly })}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  filters.privateOnly
                    ? 'bg-purple-500 text-white'
                    : 'bg-dark-600 text-gray-300 hover:bg-dark-500'
                }`}
              >
                🔒 Private Only
              </button>
            </div>
          </div>

          {/* Sorting */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sort By
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select
                value={filters.sortBy}
                onChange={(e) => updateFilters({ sortBy: e.target.value as any })}
                className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="newest">Date Created</option>
                <option value="favorites">Favorites First</option>
                <option value="spread">Spread Type</option>
                <option value="question">Question (A-Z)</option>
              </select>
              
              <select
                value={filters.sortOrder}
                onChange={(e) => updateFilters({ sortOrder: e.target.value as any })}
                className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}